import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Menu, X, CheckCircle } from 'lucide-react';
import { trackEvent, ANALYTICS_EVENTS } from '../../services/analytics';

const LandingNavbar = ({ 
  isDarkMode, 
  toggleDarkMode, 
  user, 
  onScrollToSection, 
  onOpenTrialModal 
}) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = (section, eventName) => {
    trackEvent(eventName);
    onScrollToSection(section);
    setMobileMenuOpen(false);
  };

  const handlePricingClick = () => {
    trackEvent(ANALYTICS_EVENTS.NAV_PRICING_CLICKED);
    navigate('/pricing');
    setMobileMenuOpen(false);
  };

  return (
    <nav data-testid="nav-header" className={`sticky top-0 z-50 backdrop-blur-md border-b ${isDarkMode ? 'bg-gray-900/80 border-slate-800' : 'bg-white/80 border-slate-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 group" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg group-hover:shadow-purple-500/30 transition-shadow">
              <img src="/logo.png" alt="AIQuizPark" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold font-['Outfit'] bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              AIQuizPark
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <button 
              onClick={() => handleNavClick('students-section', ANALYTICS_EVENTS.NAV_STUDENTS_CLICKED)} 
              data-testid="nav-students" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-300 hover:text-sky-400' : 'hover:bg-slate-100 text-slate-600 hover:text-sky-600'}`}
            >
              Students
            </button>
            <button 
              onClick={() => handleNavClick('teachers-section', ANALYTICS_EVENTS.NAV_TEACHERS_CLICKED)} 
              data-testid="nav-teachers" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-300 hover:text-emerald-400' : 'hover:bg-slate-100 text-slate-600 hover:text-emerald-600'}`}
            >
              Teachers
            </button>
            <button 
              onClick={() => handleNavClick('quick-challenge', ANALYTICS_EVENTS.NAV_DEMO_CLICKED)} 
              data-testid="nav-demo-quiz" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-300 hover:text-purple-400' : 'hover:bg-slate-100 text-slate-600 hover:text-purple-600'}`}
            >
              Try Demo
            </button>
            <button 
              onClick={() => handleNavClick('exam-presets', ANALYTICS_EVENTS.NAV_DEMO_CLICKED)} 
              data-testid="nav-exam-presets" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'}`}
            >
              Exams
            </button>
            <button 
              onClick={handlePricingClick} 
              data-testid="nav-pricing" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'}`}
            >
              Pricing
            </button>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            <button 
              onClick={toggleDarkMode} 
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`} 
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {user ? (
              <button 
                onClick={() => navigate('/dashboard')} 
                data-testid="nav-dashboard-button" 
                className="hidden sm:inline-flex px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-semibold text-sm hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-lg shadow-purple-500/25"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button 
                  onClick={() => navigate('/login')} 
                  data-testid="nav-login-button" 
                  className={`hidden sm:inline-flex px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  Log In
                </button>
                <button 
                  onClick={onOpenTrialModal} 
                  data-testid="nav-start-trial-button" 
                  className="hidden sm:inline-flex px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-semibold text-sm hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-lg shadow-purple-500/25"
                >
                  Start Free Trial
                </button>
              </>
            )}

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="md:hidden p-2 rounded-lg" 
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            <div className="flex flex-col space-y-2">
              <button 
                onClick={() => handleNavClick('students-section', ANALYTICS_EVENTS.NAV_STUDENTS_CLICKED)} 
                className={`px-4 py-3 text-left rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
              >
                Students
              </button>
              <button 
                onClick={() => handleNavClick('teachers-section', ANALYTICS_EVENTS.NAV_TEACHERS_CLICKED)} 
                className={`px-4 py-3 text-left rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
              >
                Teachers
              </button>
              <button 
                onClick={() => handleNavClick('quick-challenge', ANALYTICS_EVENTS.NAV_DEMO_CLICKED)} 
                className={`px-4 py-3 text-left rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
              >
                Try Demo
              </button>
              <button 
                onClick={handlePricingClick} 
                className={`px-4 py-3 text-left rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
              >
                Pricing
              </button>
              {!user && (
                <>
                  <button 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} 
                    className={`px-4 py-3 text-left rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                  >
                    Log In
                  </button>
                  <button 
                    onClick={() => { onOpenTrialModal(); setMobileMenuOpen(false); }} 
                    className="mx-4 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-semibold text-center"
                  >
                    Start Free Trial
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default LandingNavbar;
